import type { CSSProperties } from 'react';

import { useBasePath } from '../../../lib/use-base-path';
import styles from './EmptyHero.module.css';

/**
 * The two empty-state illustrations, painted as CSS backgrounds off a light + dark PNG cut.
 *
 * The artwork lives in `public/illustrations/`, so its URL MUST carry the mount base path — a
 * bare `/illustrations/…` resolves against the document root, which is only correct on the
 * root mount (symphony.wix.com). On any other mount/host (`/orion-web`, `/symphony-web`, a
 * preview host) the bare path 404s and the illustration silently vanishes, while `_next/static`
 * assets keep loading from the CDN. So the URLs are built from `useBasePath()` here — exactly
 * how the Packs empty-state cluster loads its bubbles — and handed to the stylesheet as CSS
 * custom properties, which keeps the light/dark swap a single stylesheet concern (the
 * `[data-theme='dark']` selector picks the dark var) and keeps the artwork out of the a11y tree.
 */
function heroVars(basePath: string, light: string, dark: string): CSSProperties {
  return {
    '--hero-img': `url('${basePath}/illustrations/${light}')`,
    '--hero-img-dark': `url('${basePath}/illustrations/${dark}')`,
  } as CSSProperties;
}

/**
 * The Your-Agents empty illustration: the orbit constellation carrying its own
 * "N Agents · M Skills" pill. A static export (not the live `TeamGenomeCard`) because
 * the state it illustrates is precisely the one with no agents to plot — the same
 * reason native uses a static `AgentsEmptyHero`.
 */
export function AgentsEmptyHero() {
  const basePath = useBasePath();
  return (
    <div
      aria-hidden
      className={styles.agents}
      style={heroVars(basePath, 'agents-empty-hero.png', 'agents-empty-hero-dark.png')}
    />
  );
}

/** The home empty illustration: the full-bleed conveyor of dashboard-preview cards. */
export function HomeEmptyHero() {
  const basePath = useBasePath();
  return (
    <div
      aria-hidden
      className={styles.home}
      style={heroVars(basePath, 'home-empty-hero.png', 'home-empty-hero-dark.png')}
    />
  );
}
